import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Folder, Pencil, Plus, Trash2, Users } from "lucide-react";
import remarkGfm from "remark-gfm";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import useUser from "~/contexts/user";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { StatusBadge } from "~/pages/manifold-nextgen/components/agents/StatusBadge";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { UserAvatar } from "~/pages/manifold-nextgen/components/users/UserAvatar";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import { ROUTE_NEXTGEN_VIEW, nextGenDatasetDetailPath } from "~/router/constants";
import { AgentStatus } from "~/services/agents";
import {
  ProjectShare,
  SharePrincipalType,
  deleteProject,
  projectAgentsPath,
  projectDatasetsPath,
  projectListPath,
  projectPath,
  setProjectAgents,
  setProjectDatasets,
  updateProject,
} from "~/services/projects";
import { DeleteProjectDialog } from "./DeleteProjectDialog";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { RenameProjectDialog } from "./RenameProjectDialog";
import { useProjectContext } from "./useProjectContext";

const NameTab = () => {
  const { projectId, project } = useProjectContext();
  const [renameOpen, setRenameOpen] = useState(false);

  return (
    <>
      <h2 className="text-base font-semibold text-foreground">Name</h2>

      <div className="group relative">
        <p className="text-sm text-foreground">{project.name}</p>
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 size-7 opacity-0 transition-opacity group-hover:opacity-100"
          aria-label="Edit name"
          onClick={() => setRenameOpen(true)}
        >
          <Pencil className="size-3.5" />
        </Button>
      </div>

      <RenameProjectDialog
        projectId={projectId}
        currentName={project.name}
        open={renameOpen}
        onOpenChange={setRenameOpen}
      />
    </>
  );
};

const OverviewTab = () => {
  const { projectId, project } = useProjectContext();
  const { showSuccess, showError } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    setDraft(project.description ?? "");
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProject(projectId, { description: draft });
      await mutate(projectPath(projectId));
      showSuccess("Description updated");
      setEditOpen(false);
    } catch {
      showError("Failed to update description");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h2 className="text-base font-semibold text-foreground">Description</h2>

      <div className="group relative">
        <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-em:text-foreground">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {project.description || "_No description provided._"}
          </ReactMarkdown>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 size-7 opacity-0 transition-opacity group-hover:opacity-100"
          aria-label="Edit description"
          onClick={openEdit}
        >
          <Pencil className="size-3.5" />
        </Button>
      </div>

      <Dialog open={editOpen} onOpenChange={(open) => !open && setEditOpen(false)}>
        <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-2xl" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Edit description</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
            <p className="text-xs text-muted-foreground">
              Supports Markdown: <strong>**bold**</strong>, <em>*italic*</em>, lists, and more.
            </p>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={12}
              className="font-mono text-sm focus-visible:ring-0"
              disabled={saving}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const AgentsTab = () => {
  const { projectId, attachedAgents } = useProjectContext();
  const { showSuccess } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { execute: removeAgent, isPending: isRemoving } = useAsyncMutation(
    (ids: string[]) => setProjectAgents(projectId, ids),
    {
      errorMessage: "Failed to remove agent",
      onSuccess: async () => {
        await mutate(projectAgentsPath(projectId));
        showSuccess("Agent removed");
      },
    }
  );

  const handleRemove = (agentId: string) => {
    const remaining = (attachedAgents ?? []).filter((a) => a.id !== agentId).map((a) => a.id);
    void removeAgent(remaining);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">Agents</h2>
        <Button
          variant="ghost"
          className="text-primary hover:text-primary/80"
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="size-4" />
          Add Agents
        </Button>
      </div>

      {attachedAgents === undefined ? null : attachedAgents.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">No agents attached.</p>
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {attachedAgents.map((agent) => (
            <li
              key={agent.id}
              className="group flex items-center gap-3 px-5 py-4 first:rounded-t-lg last:rounded-b-lg transition-colors hover:bg-accent/50"
            >
              <a
                href={`${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-w-0 flex-1 items-center gap-3"
              >
                <AgentAvatar id={agent.id} name={agent.name} size={32} />
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {agent.display_name}
                    </span>
                    {agent.status === AgentStatus.DISABLED && <StatusBadge status={agent.status} />}
                  </div>
                  {agent.description && (
                    <span className="truncate text-xs text-muted-foreground">
                      {agent.description}
                    </span>
                  )}
                </div>
                <ExternalLink className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </a>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                onClick={() => handleRemove(agent.id)}
                disabled={isRemoving}
                aria-label={`Remove ${agent.display_name}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <ProjectAgentsDialog
        projectId={projectId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        attached={attachedAgents ?? []}
      />
    </>
  );
};

const DatasetsTab = () => {
  const { projectId, attachedDatasets } = useProjectContext();
  const { showSuccess } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { execute: removeDataset, isPending: isRemoving } = useAsyncMutation(
    (ids: string[]) => setProjectDatasets(projectId, ids),
    {
      errorMessage: "Failed to remove dataset",
      onSuccess: async () => {
        await mutate(projectDatasetsPath(projectId));
        showSuccess("Dataset removed");
      },
    }
  );

  const handleRemove = (datasetId: string) => {
    const remaining = (attachedDatasets ?? []).filter((d) => d.id !== datasetId).map((d) => d.id);
    void removeDataset(remaining);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">Datasets</h2>
        <Button
          variant="ghost"
          className="text-primary hover:text-primary/80"
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="size-4" />
          Add Datasets
        </Button>
      </div>

      {attachedDatasets === undefined ? null : attachedDatasets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">No datasets attached.</p>
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {attachedDatasets.map((dataset) => (
            <li
              key={dataset.id}
              className="group flex items-center gap-3 px-5 py-4 first:rounded-t-lg last:rounded-b-lg transition-colors hover:bg-accent/50"
            >
              <a
                href={nextGenDatasetDetailPath(dataset.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-w-0 flex-1 items-center gap-3"
              >
                <Folder className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-foreground">
                    {dataset.title}
                  </span>
                  {dataset.description && (
                    <span className="truncate text-xs text-muted-foreground">
                      {dataset.description}
                    </span>
                  )}
                </div>
                <ExternalLink className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </a>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                onClick={() => handleRemove(dataset.id)}
                disabled={isRemoving}
                aria-label={`Remove ${dataset.title}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <ProjectDatasetsDialog
        projectId={projectId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        attachedIds={attachedDatasets?.map((d) => d.id) ?? []}
      />
    </>
  );
};

const shareName = (share: ProjectShare): string =>
  [share.given_name, share.family_name].filter(Boolean).join(" ") || share.principal_id;

const PeopleTab = () => {
  const { projectId, project } = useProjectContext();
  const currentUser = useUser();
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    shares,
    collaborators,
    isLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useProjectShare(projectId);

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">People</h2>
        <Button
          variant="ghost"
          className="text-primary hover:text-primary/80"
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="size-4" />
          Share
        </Button>
      </div>

      {shares === undefined ? null : shares.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">No one has access yet.</p>
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {shares.map((share) => {
            const isGroup = share.principal_type === SharePrincipalType.USER_TAG;
            return (
              <li
                key={`${share.principal_type}:${share.principal_id}`}
                className="flex items-center gap-3 px-5 py-4 first:rounded-t-lg last:rounded-b-lg"
              >
                {isGroup ? (
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Users className="size-4" aria-hidden="true" />
                  </div>
                ) : (
                  <UserAvatar userId={share.principal_id} size={32} />
                )}
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-foreground">
                    {shareName(share)}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {isGroup ? "Group" : share.principal_id}
                  </span>
                </div>
                <span className="shrink-0 text-xs capitalize text-muted-foreground">
                  {share.level}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <ShareModal
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        resourceLabel="project"
        resourceName={project.name}
        collaborators={collaborators}
        collaboratorsLoading={isLoading}
        collaboratorsError={collaboratorsError}
        accessLevels={accessLevels}
        defaultAccessLevel={defaultAccessLevel}
        onShare={handleShare}
        currentUserEmail={currentUser?.email}
      />
    </>
  );
};

const DangerZone = () => {
  const { projectId, project } = useProjectContext();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteProject(projectId);
      await mutate(projectListPath());
      navigate(nextgenProjectsListPath());
    } catch {
      showError("Failed to delete project. Please try again.");
    }
  };

  return (
    <>
      <h2 className="text-base font-semibold text-destructive">Delete project</h2>

      <div className="flex items-center justify-between gap-4 rounded-lg border border-destructive/30 px-4 py-4">
        <p className="text-sm text-muted-foreground">
          Deleting this project soft-deletes it and hides it from your list. Conversations, agents,
          and datasets attached to it remain intact.
        </p>
        <Button variant="destructive" className="shrink-0" onClick={() => setDeleteOpen(true)}>
          <Trash2 className="size-4" />
          Delete
        </Button>
      </div>

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectName={project.name}
        onConfirm={handleDelete}
      />
    </>
  );
};

export const ProjectSettings = () => (
  <div className="flex-1 overflow-y-auto p-6">
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      <TabHeader title="Settings" />
      <section className="flex flex-col gap-4">
        <NameTab />
      </section>
      <section className="flex flex-col gap-4">
        <OverviewTab />
      </section>
      <section className="flex flex-col gap-4">
        <AgentsTab />
      </section>
      <section className="flex flex-col gap-4">
        <DatasetsTab />
      </section>
      <section className="flex flex-col gap-4">
        <PeopleTab />
      </section>
      <section className="flex flex-col gap-4">
        <DangerZone />
      </section>
    </div>
  </div>
);
